import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import AddIcon from "@mui/icons-material/Add";
import {
  themes,
  scrollTop,
  stickyHeader,
  tableRowSize,
  StatusBadge,
} from "services/constants";
import {
  Typography,
  Box,
  Paper,
  Grid,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import { FilterProvider } from "pages/FilterContext";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import moment from "moment";

import AdvanceSalaryPanel from "./AdvanceSalaryFormPanel";
import {
  DateRange,
  ModeStandby,
  CurrencyExchange,
  LabelOutlined,
} from "@mui/icons-material";
import {
  AdvanceSummaryCards,
  advanceSummaryBucket,
  buildAdvanceSummary,
} from "./AdvanceSummaryCards";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import {
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";

// Columns matching the Request ID / Amount / Status /
// Request Date layout from the reference design.
const advanceSalaryHeader = [
  {
    key: "request_id",
    name: "Request ID",
    align: "left",
    type: "string",
    icon: <LabelOutlined fontSize="small" />,
  },
  {
    key: "effective_amount",
    name: "Amount",
    align: "left",
    type: "number",
    icon: <CurrencyExchange fontSize="small" />,
  },
  {
    key: "status",
    name: "Status",
    align: "left",
    type: "string",
    icon: <ModeStandby fontSize="small" />,
  },
  {
    key: "request_date",
    name: "Request Date",
    align: "left",
    type: "date",
    icon: <DateRange fontSize="small" />,
    showBetweenOperator: true,
    dateRangeFields: {
      fromField: "request_date",
      toField: "request_date",
    },
  },
];

const SalaryAdvanceMobileCard = ({ row, headers, onClick }) => {
  const statusHead = headers.find((head) => head.key === "status");
  const primaryHead = headers.find((head) => head.key === "request_id");
  const amountHead = headers.find((head) => head.key === "effective_amount");
  const detailHeads = headers.filter(
    (head) => !["request_id", "effective_amount", "status"].includes(head.key),
  );

  const renderValue = (head) => {
    if (head.key === "status") {
      return <StatusBadge color={row?.color_code} label={row?.status || "N/A"} />;
    }
    return (
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {row[head.key] ?? "-"}
      </Typography>
    );
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        border: "1px solid #E2E8F0",
        borderRadius: 1.5,
        p: 1.5,
        bgcolor: "#FFFFFF",
        cursor: "pointer",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, alignItems: "flex-start" }}>
        <Box sx={{ minWidth: 0 }}>
          {primaryHead ? (
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: themes.headLine }}>
              {row[primaryHead.key] ?? "-"}
            </Typography>
          ) : null}
          {amountHead ? (
            <Typography sx={{ mt: 0.35, fontSize: 12.5, color: themes.textColor }}>
              Amount: {row[amountHead.key] ?? "-"}
            </Typography>
          ) : null}
        </Box>
        {statusHead ? <Box sx={{ flexShrink: 0 }}>{renderValue(statusHead)}</Box> : null}
      </Box>
      {detailHeads.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1.25,
            borderTop: "1px solid #E2E8F0",
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1.25,
          }}
        >
          {detailHeads.map((head) => (
            <Box key={head.key} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: themes.tableBody }}>
                {head.name}
              </Typography>
              <Box sx={{ mt: 0.25 }}>{renderValue(head)}</Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const toDateIso = (value) => {
  const parsed = moment(
    value,
    ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", "DD MMM YYYY", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const AdvanceSalaryContent = ({ subModuleId }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails, topbarCompanyName, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  // No access check existed here at all -- a viewer-only user could open
  // and submit the create panel with no gate. Same editor check + "block
  // with a toast" pattern the sibling My Request screens (Leave, Off Day
  // Duty, WorkAdjustment) already use, against this module's real
  // submodule name (verified against a live "My Workspace" payload).
  const checkAccess = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Salary Advance",
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [filteredAdvances, setFilteredAdvances] = useState([]);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  // Default range is This Month (see getThisMonthRange).
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "monthEnd" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);

  // Drawer & Mode State
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("view"); // "create", "view", or "edit"
  const [selectedAdvance, setSelectedAdvance] = useState(null);

  const containerRef = useRef(null);

  const {
    result: advanceData,
    setLoad: reCallApi,
    loading,
  } = useFetch(`${domain}advance/request/?selected_user_id=${userDetails?.id}&company_id=${requestCompanyId}`);

  // Keep this screen salary-only; non-salary advance rows are handled elsewhere.
  const salaryAdvanceRows = useMemo(
    () =>
      (advanceData?.data || []).filter(
        (row) => (row?.advance_type || "Salary") === "Salary",
      ),
    [advanceData],
  );

  const dateFilteredSalaryAdvanceRows = useMemo(
    () =>
      salaryAdvanceRows.filter((row) => {
        const rowDate = toDateIso(
          row?.request_date || row?.applied_on || row?.created_at,
        );
        if (!rowDate) return true;
        return rowDate >= startDate && rowDate <= endDate;
      }),
    [endDate, salaryAdvanceRows, startDate],
  );

  useEffect(() => {
    setFilteredAdvances(dateFilteredSalaryAdvanceRows);
    setPage(0);
  }, [dateFilteredSalaryAdvanceRows]);

  useEffect(() => {
    if (userDetails?.id) reCallApi(true);
  }, [reCallApi, userDetails]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  // Status tallies for the summary cards are computed from the full request
  // set (not the currently filtered/paginated slice) so the totals stay stable
  // regardless of search or paging.
  const advanceSummary = useMemo(
    () => buildAdvanceSummary(dateFilteredSalaryAdvanceRows),
    [dateFilteredSalaryAdvanceRows],
  );

  // Narrows the CustomFilter-searched/sorted rows down to the status the
  // summary card currently selects, using the same bucketing the card counts
  // use (advanceSummaryBucket) so the rows shown always equal the number on
  // the selected card.
  const visibleAdvances = useMemo(() => {
    if (!statusCardFilter) return filteredAdvances;
    return filteredAdvances.filter(
      (row) => advanceSummaryBucket(row) === statusCardFilter,
    );
  }, [filteredAdvances, statusCardFilter]);

  const handleStatusCardClick = useCallback((key) => {
    setPage(0);
    setStatusCardFilter(key === "Total Requests" ? "" : key);
  }, []);

  const handleDateRangeChange = useCallback((rangeStart, rangeEnd, rangeLabel) => {
    setStartDate(moment(rangeStart).format("YYYY-MM-DD"));
    setEndDate(moment(rangeEnd).format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  }, []);

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const advanceSalaryColumnHeaders = useMemo(() => advanceSalaryHeader, []);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "myzone_advance_salary_saved_views",
    fullHeaders: advanceSalaryColumnHeaders,
    screenAccess: true,
    onViewChange: handleSavedViewChange,
  });

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setFilteredAdvances(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  // 3. Handlers for different modes
  const handleCreate = () => {
    if (!checkAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to apply for a salary advance",
      );
      return;
    }
    setPanelMode("create");
    setSelectedAdvance(null);
    setIsPanelOpen(true);
  };

  const handleView = (row) => {
    setPanelMode("view");
    setSelectedAdvance(row);
    setIsPanelOpen(true);
  };

  const handleEdit = () => {
    setPanelMode("edit"); // Keeps the drawer open, just switches the UI mode
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setTimeout(() => setSelectedAdvance(null), 300); // Clear data after drawer closes
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  if (loading) return <Loader />;

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Salary Advance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and manage your salary advance requests.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <ApprovalDateRangeFilter
            startDate={startDate}
            endDate={endDate}
            dateRangeLabel={dateRangeLabel}
            onChange={handleDateRangeChange}
            includeCurrentDate
            showToday
          />
          {checkAccess ? (
            <PrimaryButton
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 0, px: 2, whiteSpace: "nowrap" }}
              onClick={handleCreate}
            >
              New Request
            </PrimaryButton>
          ) : null}
        </Stack>
      </Box>

      <AdvanceSummaryCards
        summary={advanceSummary}
        activeFilter={statusCardFilter}
        onCardClick={handleStatusCardClick}
        totalRequestsDetail={dateRangeLabel}
      />

      <Paper
        sx={{
          width: "100%",
          // Size to the rows; MyZoneApply scrolls when content exceeds its height.
        }}
      >
        <CustomFilter
          title={"Salary Advance Records"}
          titleOverride={titleOverride}
          filterHeaders={visibleHeaders}
          data={dateFilteredSalaryAdvanceRows}
          filteredData={visibleAdvances}
          onFilteredDataChange={handleFilteredDataChange}
          screenAccess={true}
          showtitle_count={true}
          showFilter={false}
          filters={null}
          hideCount={true}
          downloadFormats={["excel", "pdf", "csv"]}
          includeMetadata={true}
          metadata={reportMetadata}
          labeledToolbar={true}
          searchPlaceholder="Search by request ID, amount, reason..."
        />

        <Grid sx={{ ml: { xs: 1.5, sm: 2 }, mr: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: { xs: "grid", sm: "none" }, gap: 1.25 }}>
            {visibleAdvances?.length === 0 ? (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                  color: themes.tableBody,
                  border: "1px dashed #E2E8F0",
                  borderRadius: 1.5,
                }}
              >
                No salary advance records found.
              </Box>
            ) : (
              visibleAdvances
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                ?.map((row, index) => (
                  <SalaryAdvanceMobileCard
                    key={row?.id ?? index}
                    row={row}
                    headers={visibleHeaders}
                    onClick={() => handleView(row)}
                  />
                ))
            )}
          </Box>
          <TableContainer
            ref={containerRef}
            sx={{
              // No fixed maxHeight — a capped height forced its own
              // scrollbar even with only a few rows; scroll with the page
              // instead, matching the other My Workspace request screens.
              overflowY: "visible",
              display: { xs: "none", sm: "block" },
            }}
          >
            <Table>
              {/* Header Styled to match screenshot */}
              <TableHead sx={{ ...stickyHeader, bgcolor: "#f5f5f5" }}>
                <TableRow sx={tableRowSize}>
                  {visibleHeaders?.map((value) => (
                    <TableCell
                      key={value.key}
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        borderBottom: "1px solid #ddd",
                        bgcolor: "#f5f5f5", // Light grey header from design
                      }}
                      align={value.align || "center"}
                    >
                      {value.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleAdvances?.length === 0 ? (
                  <TableEmptyState dataLength={advanceData?.data || []} />
                ) : (
                  visibleAdvances
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                    ?.map((row, index) => (
                      <React.Fragment key={index}>
                        <TableRow
                          sx={{
                            ...tableRowSize,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#fafafa" },
                          }}
                          onClick={() => handleView(row)} // Click anywhere on row to view
                        >
                          {visibleHeaders.map((head) => (
                            <TableCell
                              key={head.key}
                              align={head.align || "center"}
                              sx={{ borderBottom: "1px solid #f0f0f0" }}
                            >
                              {head.key === "status" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                  }}
                                >
                                  <StatusBadge
                                    color={row?.color_code}
                                    label={row?.status || "N/A"}
                                  />
                                </Box>
                              ) : (
                                <Typography variant="body2">
                                  {row[head.key] ?? "-"}
                                </Typography>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </React.Fragment>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 1, p: 1 }}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: visibleAdvances?.length || 0,
              itemLabel: "records",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Grid>
      </Paper>

      {/* Unified Side Panel */}
      {isPanelOpen && (
        <AdvanceSalaryPanel
          open={isPanelOpen}
          mode={panelMode} // Passes 'create', 'view', or 'edit'
          initialData={selectedAdvance} // The clicked row's data
          onClose={handlePanelClose}
          onEdit={handleEdit} // Passed to View mode so it can trigger Edit
          reCallApi={reCallApi}
          checkAccess={checkAccess}
        />
      )}
    </Box>
  );
};

export const AdvanceSalary = ({ subModuleId: parentSubModuleId }) => {
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );

  const advModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "My Request",
  );
  const subModuleId =
    parentSubModuleId ||
    advModule?.submodules?.find((sub) =>
      ["Advance Salary", "Salary Advance"].includes(sub.sub_module_name),
    )?.sub_module_id;

  return (
    <FilterProvider
      domain={domain}
      userDetails={userDetails}
      subModuleId={subModuleId}
    >
      <AdvanceSalaryContent subModuleId={subModuleId} />
    </FilterProvider>
  );
};
